import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import DataTable from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography from "@insite/mobius/Typography";
import { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Block from "@store/Components/Common/Block";
import StoreInvoiceDetailPageTypeLink from "@store/Components/Store/StoreInvoiceDetailPageTypeLink";
import { WIDGET_GROUP } from "@store/constants";
import loadInvoicePaymentConfirmation from "@store/Handlers/CommonHandlers/LoadInvoicePaymentConfirmation";
import { InvoicePaymentConfirmationPageContext } from "@store/Pages/InvoicePaymentConfirmationPage";
import { contentSpacingStyles, sideBoxStyles } from "@store/theme/common/mixins";
import { checkoutBlockProps, checkoutInfoGridProps, checkoutInfoProps } from "@store/theme/common/props/checkout";
import { TYPOGRAPHY } from "@store/theme/design";
import { InvoicePaymentConfirmationModel, InvoicePaymentPaidInvoiceModel } from "@store/Types/InvoicePayment";
import getTokenDisplayName from "@store/utilities/getTokenDisplayName";
import getWidgetComponent from "@store/utilities/getWidgetComponent";
import { InfosBoxType } from "@store/Widgets/InfosBox";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const InfosBox = getWidgetComponent("InfosBox");

type Props = WidgetProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const parsedQuery = parseQueryString<{ paymentOutcomeId: string }>(location.search);
    const paymentOutcomeId = parsedQuery.paymentOutcomeId;
    const language = state.context.session.language;

    return { paymentOutcomeId, language };
};

const mapDispatchToProps = {
    loadInvoicePaymentConfirmation,
};

export interface InvoicePaymentConfirmationViewStyles {
    infoGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    infoLabel?: TypographyPresentationProps;
    infoValue?: TypographyPresentationProps;
    paymentInformationWrapper?: InjectableCss;
    informationTitle?: TypographyPresentationProps;
    informationTitleWrapper?: InjectableCss;
    paymentInformationItemsWrapper?: InjectableCss;
    paymentInformationItem?: SmallHeadingAndTextStyles;
    dataTableWrapper?: InjectableCss;
    loadingWrapper?: InjectableCss;
}

const styles: InvoicePaymentConfirmationViewStyles = {
    infoGridContainer: checkoutInfoGridProps.container,
    infoGridItem: checkoutInfoGridProps.item,
    infoLabel: checkoutInfoProps.label,
    infoValue: checkoutInfoProps.value,
    paymentInformationWrapper: {
        css: css`
            ${contentSpacingStyles()}
            ${sideBoxStyles()}
        `,
    },
    informationTitle: {
        variant: "h6",
    },
    informationTitleWrapper: {},
    paymentInformationItemsWrapper: {
        css: css`
            margin: -${TYPOGRAPHY.spacing.base} -${TYPOGRAPHY.spacing.base} 0 0;
            display: flex;
            flex-wrap: wrap;
        `,
    },
    dataTableWrapper: {
        css: css`
            overflow-x: auto;
        `,
    },
    loadingWrapper: {
        css: css`
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
};

const InvoicePaymentConfirmationView: React.FC<Props> = ({
    loadInvoicePaymentConfirmation,
    paymentOutcomeId,
    language,
}) => {
    const [paymentConfirmation, setPaymentConfirmation] = React.useState<InvoicePaymentConfirmationModel | null>(null);
    const [isError, setIsError] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);

    React.useEffect(() => {
        setIsLoading(true);
        if (paymentOutcomeId) {
            loadInvoicePaymentConfirmation({
                paymentOutcomeId,
                onSuccess: result => {
                    setPaymentConfirmation(result);
                    setIsLoading(false);
                },
                onError: error => {
                    setIsError(true);
                    setIsLoading(false);
                },
            });
        } else {
            setIsError(true);
            setIsLoading(false);
        }
    }, []);

    if (isLoading) {
        return (
            <StyledWrapper {...styles.loadingWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    if (isError) {
        return (
            <InfosBox
                type={InfosBoxType.Warning}
                content={translate("We could not find your payment details or your payment may not exists.")}
            />
        );
    }

    if (!paymentConfirmation) {
        return null;
    }

    return (
        <StyledWrapper>
            <InfosBox
                infosType={InfosBoxType.Success}
                content={translate("Payment Complete")}
            />
            <Block {...checkoutBlockProps}>
                <GridContainer {...styles.infoGridContainer}>
                    <GridItem {...styles.infoGridItem}>
                        <Typography {...styles.infoLabel}>{translate("Confirmation #")}</Typography>
                        <Typography {...styles.infoValue}>{paymentConfirmation.confirmationId}</Typography>
                    </GridItem>
                    <GridItem {...styles.infoGridItem}>
                        <Typography {...styles.infoLabel}>{translate("Payment Amount")}</Typography>
                        <Typography {...styles.infoValue}>{paymentConfirmation.paymentAmountDisplay}</Typography>
                    </GridItem>
                    <GridItem {...styles.infoGridItem}>
                        <Typography {...styles.infoLabel}>{translate("Paid From Account")}</Typography>
                        <Typography {...styles.infoValue}>{getTokenDisplayName(paymentConfirmation.account.name)}</Typography>
                    </GridItem>
                    <GridItem {...styles.infoGridItem}>
                        <Typography {...styles.infoLabel}>{translate("Payment Date")}</Typography>
                        <LocalizedDateTime dateTime={new Date(paymentConfirmation.paymentDate)} />
                    </GridItem>
                </GridContainer>
            </Block>
            <Block {...checkoutBlockProps}>
                <StyledWrapper {...styles.dataTableWrapper}>
                    <DataTable>
                        <DataTableHead>
                            <DataTableHeader title={translate("Id")}>{translate("Invoice #")}</DataTableHeader>
                            <DataTableHeader>{translate("Date")}</DataTableHeader>
                            <DataTableHeader>{translate("Balance")}</DataTableHeader>
                            <DataTableHeader>{translate("Paid")}</DataTableHeader>
                        </DataTableHead>
                        <DataTableBody>
                            {paymentConfirmation.paidInvoices.map((invoice: InvoicePaymentPaidInvoiceModel) => (
                                <DataTableRow key={invoice.id}>
                                    <DataTableCell>
                                        <StoreInvoiceDetailPageTypeLink title={invoice.id} invoiceNumber={invoice.id} isLink={true}/>
                                    </DataTableCell>
                                    <DataTableCell>
                                        <LocalizedDateTime dateTime={new Date(invoice.dateInvoiced)} />
                                    </DataTableCell>
                                    <DataTableCell>{invoice.balanceDisplay}</DataTableCell>
                                    <DataTableCell>{invoice.paymentAmountDisplay}</DataTableCell>
                                </DataTableRow>
                            ))}
                        </DataTableBody>
                    </DataTable>
                </StyledWrapper>
            </Block>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(InvoicePaymentConfirmationView),
    definition: {
        group: WIDGET_GROUP.invoicePayment as any,
        displayName: "Invoice Payment View",
        icon: "heading",
        allowedContexts: [InvoicePaymentConfirmationPageContext] as any[],
    },
};

export default widgetModule;
